/**
 * Shared recovery for stale lazy-chunk failures after a deployment.
 *
 * After a new build the hashed chunk filenames change. A client still running
 * the previous build — or holding a stale service-worker cache — requests a
 * chunk that now 404s. The browser raises a `ChunkLoadError` / "Loading chunk
 * ... failed", the in-flight navigation to a lazy route (e.g. /setting/setup/*)
 * throws, and the SPA hangs.
 *
 * The failure surfaces two ways, and BOTH must recover identically:
 *  - the Router emits a `NavigationError` (handled in AppComponent), and
 *  - the global `ErrorHandler` may receive the raw ChunkLoadError.
 * A plain reload is NOT enough while a service worker is active (production):
 * the SW re-serves the same stale cached index.html + chunks, so the 404
 * repeats and the tab reload-loops / hangs. We purge CacheStorage and
 * unregister the worker first, then reload so the browser fetches the fresh
 * build from the network. Both paths share one guard so recovery runs at most
 * once per session and can never spin into an infinite reload loop.
 */

export const CHUNK_LOAD_ERROR =
  /Loading chunk [\w-]+ failed|ChunkLoadError|Loading CSS chunk|error loading dynamically imported module|Importing a module script failed/i;

// sessionStorage flag so a permanently-failing chunk reloads at most once.
// Shared by every recovery path — keep this the single source of truth.
export const RELOAD_GUARD_KEY = 'chunk-reload-attempted';

/** True when the error is a failed lazy chunk / dynamic-import fetch. */
export function isChunkLoadError(error: any): boolean {
  const message: string = error?.message || error?.toString?.() || '';
  return CHUNK_LOAD_ERROR.test(message);
}

/**
 * Purge SW caches + unregister workers, then hard reload. Guarded to run at
 * most once per session. Safe to call from multiple paths — the first caller
 * sets the guard and reloads; later callers no-op.
 */
export function recoverFromStaleChunk(): void {
  try {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) {
      console.error('Stale chunk persists after reload; aborting auto-recovery.');
      return;
    }
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1');
  } catch {
    // sessionStorage unavailable (private mode / SSR) — fall through to reload.
  }

  purgeServiceWorkerCaches().finally(() => window.location.reload());
}

/** Best-effort: delete all CacheStorage entries and unregister service workers. */
async function purgeServiceWorkerCaches(): Promise<void> {
  try {
    if (typeof caches !== 'undefined' && caches?.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    }
    await unregisterAllServiceWorkers();
  } catch {
    // Ignore — reload happens regardless via the caller's finally().
  }
}

/** Best-effort: unregister every service worker registered for this origin. */
async function unregisterAllServiceWorkers(): Promise<boolean> {
  if (!navigator?.serviceWorker?.getRegistrations) {
    return false;
  }
  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.all(registrations.map(registration => registration.unregister()));
  return registrations.length > 0;
}

// sessionStorage flag so the dev-server SW cleanup reloads at most once per session.
export const DEV_SW_CLEANUP_KEY = 'dev-sw-unregistered';

/**
 * True when the app is being served by a local `ng serve` dev server.
 *
 * Deliberately a hostname check rather than `isDevMode()`: the serve configurations that
 * cause the problem (`dev-deploy`, `staging`, `live`) build with `optimization: true` and
 * an `environment.production === true` file, so Angular does not consider them dev mode.
 */
export function isLocalDevServer(): boolean {
  if (typeof window === 'undefined' || !window.location) {
    return false;
  }
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host === '::1' || host === '[::1]';
}

/**
 * Removes any service worker a previous `ng serve` run installed on localhost.
 *
 * `serve:dev-deploy` / `serve:staging` / `serve:live` point at build configurations with
 * `serviceWorker: true` and `outputHashing: 'all'`, and their environment files set
 * `production: true` — so a real ngsw worker registers against the dev server. Each rebuild
 * re-hashes the chunk filenames while the installed worker keeps serving its cached manifest,
 * so a lazy route the worker has not cached (e.g. `/setting/setup/*`) gets intercepted and the
 * request never settles. Nothing rejects, so no ChunkLoadError is raised and
 * `recoverFromStaleChunk` never runs: the router simply hangs mid-navigation with the loader
 * up and a clean console, and it survives a fresh tab because the registration is persistent.
 *
 * Registration is now disabled on localhost (see `AppModule`), but that alone cannot help a
 * browser that already has the worker installed — an active worker keeps intercepting until
 * the page reloads without it. This clears it and reloads once so those sessions self-heal.
 */
export async function unregisterDevServerServiceWorker(): Promise<void> {
  if (!isLocalDevServer() || typeof navigator === 'undefined') {
    return;
  }

  try {
    const hadRegistrations = await unregisterAllServiceWorkers();
    if (!hadRegistrations) {
      return;
    }

    if (typeof caches !== 'undefined' && caches?.keys) {
      const keys = await caches.keys();
      await Promise.all(keys.map(key => caches.delete(key)));
    }

    // A worker that is already controlling this page keeps serving its stale cache until the
    // page is reloaded without it. Guarded so this can never become a reload loop.
    if (navigator.serviceWorker?.controller && !sessionStorage.getItem(DEV_SW_CLEANUP_KEY)) {
      sessionStorage.setItem(DEV_SW_CLEANUP_KEY, '1');
      window.location.reload();
    }
  } catch {
    // Best-effort cleanup — never block app start.
  }
}
